import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Mail, 
  Calendar,
  ExternalLink,
  RefreshCw,
  GraduationCap,
  Church,
  UserCheck,
  Ban,
  Undo2
} from 'lucide-react';

const EstudiantesPendientes = () => {
  const { user, API } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [pendingStudents, setPendingStudents] = useState([]);
  const [pendingApproval, setPendingApproval] = useState([]);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingApproval, setLoadingApproval] = useState(true);
  const [loadingRejected, setLoadingRejected] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [formularioConfig, setFormularioConfig] = useState(null);
  
  // Leer la pestaña inicial de la URL (?tab=estudiantes | graduados-pastores | rechazados)
  const tabFromUrl = searchParams.get('tab');
  const validTabs = ['estudiantes', 'graduados-pastores', 'rechazados'];
  const [activeTab, setActiveTab] = useState(validTabs.includes(tabFromUrl) ? tabFromUrl : 'estudiantes');

  useEffect(() => {
    if (user?.rol === 'admin' || user?.permisos_adicionales?.promotor_iba) {
      loadPendingStudents();
      loadFormularioConfig();
    }
    if (user?.rol === 'admin') {
      loadPendingApproval();
      loadRejectedUsers();
    }
  }, [user]);

  // Actualizar la pestaña cuando cambie la URL
  useEffect(() => {
    const newTab = searchParams.get('tab');
    if (validTabs.includes(newTab)) {
      setActiveTab(newTab);
    }
  }, [searchParams]);

  const loadPendingStudents = async () => {
    try {
      const response = await fetch(`${API}/admin/estudiantes-pendientes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingStudents(data.estudiantes_pendientes || []);
      } else {
        toast.error('Error al cargar estudiantes pendientes');
      }
    } catch (error) {
      console.error('Error loading pending students:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingApproval = async () => {
    try {
      const response = await fetch(`${API}/admin/usuarios-pendientes-aprobacion`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingApproval(data.usuarios_pendientes || []);
      } else {
        console.error('Error loading pending approval users');
      }
    } catch (error) {
      console.error('Error loading pending approval:', error);
    } finally {
      setLoadingApproval(false);
    }
  };

  const loadRejectedUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/usuarios-rechazados`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRejectedUsers(data.usuarios_rechazados || []);
      } else {
        console.error('Error loading rejected users');
      }
    } catch (error) {
      console.error('Error loading rejected users:', error);
    } finally {
      setLoadingRejected(false);
    }
  };

  // Restaurar un usuario rechazado a estado pendiente (vuelve a la pestaña original)
  const handleRestoreUser = async (userId, userName) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API}/admin/restaurar-pendiente/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        toast.success(`${userName} fue restaurado a pendiente`);
        // Recargar las 3 listas: el usuario desaparece de Rechazados y vuelve a su pestaña original
        loadRejectedUsers();
        loadPendingStudents();
        loadPendingApproval();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Error al restaurar usuario');
      }
    } catch (error) {
      toast.error('Error al restaurar usuario');
      console.error('Restore error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const loadFormularioConfig = async () => {
    try {
      const response = await fetch(`${API}/formulario-config`);
      if (response.ok) {
        const data = await response.json();
        setFormularioConfig(data);
      }
    } catch (error) {
      console.error('Error loading formulario config:', error);
    }
  };

  // Aprobar estudiante (pendiente de formulario)
  const handleApproveStudent = async (studentId, studentName) => {
    setActionLoading(studentId);
    try {
      const response = await fetch(`${API}/admin/aprobar-estudiante/${studentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success(`${studentName} ha sido aprobado exitosamente`);
        loadPendingStudents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al aprobar estudiante');
      }
    } catch (error) {
      toast.error('Error al aprobar estudiante');
      console.error('Approve error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Rechazar estudiante (pendiente de formulario)
  const handleRejectStudent = async (studentId, studentName) => {
    setActionLoading(studentId);
    try {
      const response = await fetch(`${API}/admin/rechazar-estudiante/${studentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success(`${studentName} ha sido rechazado`);
        loadPendingStudents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al rechazar estudiante');
      }
    } catch (error) {
      toast.error('Error al rechazar estudiante');
      console.error('Reject error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Aprobar usuario (graduado/pastor pendiente de aprobación)
  const handleApproveUser = async (userId, userName) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API}/admin/aprobar-usuario/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success(`${userName} ha sido aprobado exitosamente`);
        loadPendingApproval();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al aprobar usuario');
      }
    } catch (error) {
      toast.error('Error al aprobar usuario');
      console.error('Approve error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Rechazar usuario (graduado/pastor pendiente de aprobación)
  const handleRejectUser = async (userId, userName) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API}/admin/rechazar-usuario/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success(`${userName} ha sido rechazado`);
        loadPendingApproval();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al rechazar usuario');
      }
    } catch (error) {
      toast.error('Error al rechazar usuario');
      console.error('Reject error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (rol) => {
    const config = {
      estudiante: { label: '🎓 Estudiante', className: 'bg-green-50 text-green-700 border-green-200' },
      pastor: { label: '⛪ Pastor/Líder', className: 'bg-purple-50 text-purple-700 border-purple-200' },
      graduado: { label: '🎓 Graduado IBA', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      profesor: { label: '👨‍🏫 Profesor', className: 'bg-blue-50 text-blue-700 border-blue-200' }
    };
    const roleConfig = config[rol] || { label: rol, className: 'bg-gray-50 text-gray-700' };
    return <Badge variant="outline" className={roleConfig.className}>{roleConfig.label}</Badge>;
  };

  const handleRefresh = () => {
    setLoading(true);
    setLoadingApproval(true);
    setLoadingRejected(true);
    loadPendingStudents();
    loadPendingApproval();
    loadRejectedUsers();
  };

  // Verificar permisos
  const esAdmin = user?.rol === 'admin';
  const esPromotor = user?.permisos_adicionales?.promotor_iba;
  
  if (!esAdmin && !esPromotor) {
    return (
      <div className="p-4">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-semibold text-red-600 mb-2">Acceso Denegado</h3>
            <p className="text-gray-600">Solo administradores y promotores IBA pueden gestionar usuarios pendientes.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalPendientes = pendingStudents.length + pendingApproval.length;

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <UserCheck className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-800">Usuarios Pendientes</h2>
              <Badge variant="secondary" className="ml-2 bg-orange-100 text-orange-800">
                {totalPendientes} total
              </Badge>
            </div>
            <p className="text-gray-600">
              Gestiona la aprobación de nuevos usuarios del sistema.
            </p>
          </div>
          
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={loading || loadingApproval}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${(loading || loadingApproval) ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full ${esAdmin ? 'grid-cols-3' : 'grid-cols-2'} mb-6`}>
          <TabsTrigger 
            value="estudiantes" 
            className="data-[state=active]:bg-green-600 data-[state=active]:text-white"
          >
            <GraduationCap className="h-4 w-4 mr-2" />
            Estudiantes
            {pendingStudents.length > 0 && (
              <Badge className="ml-2 bg-green-700 text-white text-xs">
                {pendingStudents.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="graduados-pastores" 
            className="data-[state=active]:bg-amber-600 data-[state=active]:text-white"
            disabled={!esAdmin}
          >
            <Church className="h-4 w-4 mr-2" />
            Pastores y Graduados
            {pendingApproval.length > 0 && (
              <Badge className="ml-2 bg-amber-700 text-white text-xs">
                {pendingApproval.length}
              </Badge>
            )}
          </TabsTrigger>
          {esAdmin && (
            <TabsTrigger 
              value="rechazados" 
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
              data-testid="tab-rechazados"
            >
              <Ban className="h-4 w-4 mr-2" />
              Rechazados
              {rejectedUsers.length > 0 && (
                <Badge className="ml-2 bg-red-700 text-white text-xs">
                  {rejectedUsers.length}
                </Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab 1: Estudiantes pendientes de formulario */}
        <TabsContent value="estudiantes">
          {/* Info Card - Formulario */}
          {formularioConfig && (
            <Card className="mb-6 border-l-4 border-l-green-500 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">
                      📋 {formularioConfig.titulo}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {formularioConfig.descripcion}
                    </p>
                    <p className="text-xs text-green-700 mt-2">
                      Los estudiantes deben completar este formulario antes de ser aprobados.
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(formularioConfig.link_formulario, '_blank')}
                    className="border-green-300 hover:bg-green-100"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Ver Formulario
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-40 bg-gray-200 rounded"></div>
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          ) : pendingStudents.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  ¡No hay estudiantes pendientes!
                </h3>
                <p className="text-gray-600">
                  Todos los estudiantes han completado el proceso de registro.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingStudents.map((student) => (
                <Card key={student.id} className="hover:shadow-lg transition-shadow border-l-4 border-l-green-400">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">
                        {student.nombre} {student.apellido}
                      </CardTitle>
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                        <Clock className="h-3 w-3 mr-1" />
                        Formulario
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2" />
                      {student.email}
                    </div>
                    
                    {student.celular && (
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="fas fa-phone h-4 w-4 mr-2 text-gray-400"></i>
                        {student.celular}
                      </div>
                    )}
                    
                    {student.nombre_iglesia && (
                      <div className="flex items-center text-sm text-cyan-600">
                        <i className="fas fa-church h-4 w-4 mr-2"></i>
                        {student.nombre_iglesia}
                      </div>
                    )}
                    
                    {student.nombre_pastor && (
                      <div className="flex items-center text-sm text-cyan-600">
                        <i className="fas fa-user-tie h-4 w-4 mr-2"></i>
                        {student.nombre_pastor}
                      </div>
                    )}
                    
                    <div className="flex items-center text-sm">
                      <i className="fas fa-user-tag h-4 w-4 mr-2 text-gray-400"></i>
                      {getRoleBadge(student.rol)}
                    </div>
                    
                    {student.codigo_referido_usado && (
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="fas fa-user-friends h-4 w-4 mr-2 text-gray-400"></i>
                        Referido: <span className="font-mono ml-1 bg-gray-100 px-1 rounded text-xs">{student.codigo_referido_usado}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDate(student.fecha_registro)}
                    </div>

                    <div className="flex gap-2 pt-3">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            size="sm" 
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            disabled={actionLoading === student.id}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Aprobar
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Aprobar Estudiante</AlertDialogTitle>
                            <AlertDialogDescription>
                              ¿Estás seguro que quieres aprobar a <strong>{student.nombre} {student.apellido}</strong>?
                              <br /><br />
                              El estudiante podrá acceder completamente al sistema.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleApproveStudent(student.id, `${student.nombre} ${student.apellido}`)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Aprobar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            disabled={actionLoading === student.id}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Rechazar
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Rechazar Estudiante</AlertDialogTitle>
                            <AlertDialogDescription>
                              ¿Estás seguro que quieres rechazar a <strong>{student.nombre} {student.apellido}</strong>?
                              <br /><br />
                              Esta acción desactivará la cuenta.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleRejectStudent(student.id, `${student.nombre} ${student.apellido}`)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Rechazar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>

                    {actionLoading === student.id && (
                      <div className="text-center text-sm text-gray-500">
                        <RefreshCw className="h-4 w-4 animate-spin inline mr-1" />
                        Procesando...
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab 2: Pastores y Graduados pendientes de aprobación */}
        <TabsContent value="graduados-pastores">
          {/* Info Card - Aprobación */}
          <Card className="mb-6 border-l-4 border-l-amber-500 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 rounded-full p-2">
                  <GraduationCap className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">
                    ¿Qué son los usuarios pendientes de aprobación?
                  </h3>
                  <p className="text-sm text-gray-600">
                    Los <strong>Graduados de IBA</strong> y <strong>Pastores/Líderes</strong> que se registran por su cuenta 
                    requieren verificación manual. Una vez aprobados, podrán acceder a sus áreas exclusivas y 
                    beneficios especiales como inscripción a Clases Abiertas.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {loadingApproval ? (
            <div className="animate-pulse space-y-4">
              <div className="h-40 bg-gray-200 rounded"></div>
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          ) : pendingApproval.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  ¡No hay usuarios pendientes de aprobación!
                </h3>
                <p className="text-gray-600">
                  Todos los graduados y pastores han sido revisados.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingApproval.map((pendingUser) => (
                <Card key={pendingUser.id} className="hover:shadow-lg transition-shadow border-l-4 border-l-amber-400">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">
                        {pendingUser.nombre} {pendingUser.apellido}
                      </CardTitle>
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                        <Clock className="h-3 w-3 mr-1" />
                        Aprobación
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2" />
                      {pendingUser.email}
                    </div>
                    
                    {pendingUser.telefono && (
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="fas fa-phone h-4 w-4 mr-2 text-gray-400"></i>
                        {pendingUser.telefono}
                      </div>
                    )}

                    {pendingUser.nombre_iglesia && (
                      <div className="flex items-center text-sm text-cyan-600">
                        <i className="fas fa-church h-4 w-4 mr-2"></i>
                        {pendingUser.nombre_iglesia}
                      </div>
                    )}
                    
                    {pendingUser.nombre_pastor && (
                      <div className="flex items-center text-sm text-cyan-600">
                        <i className="fas fa-user-tie h-4 w-4 mr-2"></i>
                        {pendingUser.nombre_pastor}
                      </div>
                    )}

                    {pendingUser.pais && (
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="fas fa-globe h-4 w-4 mr-2 text-gray-400"></i>
                        {pendingUser.pais}
                      </div>
                    )}
                    
                    <div className="flex items-center text-sm">
                      <i className="fas fa-user-tag h-4 w-4 mr-2 text-gray-400"></i>
                      {getRoleBadge(pendingUser.rol)}
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDate(pendingUser.fecha_registro)}
                    </div>

                    <div className="flex gap-2 pt-3">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            size="sm" 
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            disabled={actionLoading === pendingUser.id}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Aprobar
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Aprobar {pendingUser.rol === 'graduado' ? 'Graduado' : 'Pastor'}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              ¿Estás seguro que quieres aprobar a <strong>{pendingUser.nombre} {pendingUser.apellido}</strong>?
                              <br /><br />
                              {pendingUser.rol === 'graduado' ? (
                                <>El graduado podrá acceder al área VIP, ver grabaciones y devocionales exclusivos, e inscribirse a Clases Abiertas.</>
                              ) : (
                                <>El pastor podrá inscribirse a Clases Abiertas y acceder a todas las funcionalidades.</>
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleApproveUser(pendingUser.id, `${pendingUser.nombre} ${pendingUser.apellido}`)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Aprobar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            disabled={actionLoading === pendingUser.id}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Rechazar
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Rechazar Usuario</AlertDialogTitle>
                            <AlertDialogDescription>
                              ¿Estás seguro que quieres rechazar a <strong>{pendingUser.nombre} {pendingUser.apellido}</strong>?
                              <br /><br />
                              Esta acción desactivará la cuenta.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleRejectUser(pendingUser.id, `${pendingUser.nombre} ${pendingUser.apellido}`)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Rechazar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>

                    {actionLoading === pendingUser.id && (
                      <div className="text-center text-sm text-gray-500">
                        <RefreshCw className="h-4 w-4 animate-spin inline mr-1" />
                        Procesando...
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab 3: Usuarios rechazados — admin puede restaurarlos a pendiente */}
        <TabsContent value="rechazados">
          <Card className="mb-6 border-l-4 border-l-red-500 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="bg-red-100 rounded-full p-2">
                  <Ban className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">
                    Usuarios rechazados
                  </h3>
                  <p className="text-sm text-gray-600">
                    Si rechazaste un usuario por error, podés <strong>restaurarlo a pendiente</strong>.
                    Vuelve a su pestaña original (Estudiantes o Pastores/Graduados) y podrás aprobarlo desde ahí.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {loadingRejected ? (
            <div className="animate-pulse space-y-4">
              <div className="h-40 bg-gray-200 rounded"></div>
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          ) : rejectedUsers.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  No hay usuarios rechazados
                </h3>
                <p className="text-gray-600">
                  Cuando rechaces un usuario, aparecerá acá por si querés revertir la acción.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rechazados-grid">
              {rejectedUsers.map((rejUser) => (
                <Card key={rejUser.id} className="hover:shadow-lg transition-shadow border-l-4 border-l-red-400">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">
                        {rejUser.nombre} {rejUser.apellido}
                      </CardTitle>
                      <Badge variant="secondary" className="bg-red-100 text-red-800">
                        <Ban className="h-3 w-3 mr-1" />
                        Rechazado
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2" />
                      {rejUser.email}
                    </div>

                    {(rejUser.celular || rejUser.telefono) && (
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="fas fa-phone h-4 w-4 mr-2 text-gray-400"></i>
                        {rejUser.celular || rejUser.telefono}
                      </div>
                    )}

                    {rejUser.nombre_iglesia && (
                      <div className="flex items-center text-sm text-cyan-600">
                        <i className="fas fa-church h-4 w-4 mr-2"></i>
                        {rejUser.nombre_iglesia}
                      </div>
                    )}

                    <div className="flex items-center text-sm">
                      <i className="fas fa-user-tag h-4 w-4 mr-2 text-gray-400"></i>
                      {getRoleBadge(rejUser.rol)}
                    </div>

                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      Registrado: {formatDate(rejUser.fecha_registro)}
                    </div>

                    <div className="pt-3">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            disabled={actionLoading === rejUser.id}
                            data-testid={`btn-restaurar-${rejUser.id}`}
                          >
                            <Undo2 className="h-4 w-4 mr-1" />
                            Restaurar a Pendiente
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Restaurar Usuario</AlertDialogTitle>
                            <AlertDialogDescription>
                              ¿Querés restaurar a <strong>{rejUser.nombre} {rejUser.apellido}</strong> al estado pendiente?
                              <br /><br />
                              {rejUser.rol === 'graduado'
                                ? 'Volverá a la pestaña "Pastores y Graduados" pendiente de aprobación.'
                                : rejUser.rol === 'pastor'
                                  ? 'Volverá a la pestaña "Estudiantes" pendiente de completar formulario.'
                                  : 'Volverá a la pestaña "Estudiantes" pendiente de completar formulario.'}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRestoreUser(rejUser.id, `${rejUser.nombre} ${rejUser.apellido}`)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              Restaurar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>

                    {actionLoading === rejUser.id && (
                      <div className="text-center text-sm text-gray-500">
                        <RefreshCw className="h-4 w-4 animate-spin inline mr-1" />
                        Procesando...
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EstudiantesPendientes;
